import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { PackagingPpwrComplianceLevel } from '@/shared/hooks/api/packagingSuppliers'

export const PACKAGING_PPWR_COMPLIANCE_LEVELS: PackagingPpwrComplianceLevel[] = [
  'NONE',
  'SUFFICIENTLY_MINIMIZED',
  'NOT_SUFFICIENTLY_MINIMIZED',
]

export type PpwrComplianceLabels = {
  heavyMetals: string
  substancesOfConcern: string
  none: string
  sufficientlyMinimized: string
  heavyMetalsSufficientlyMinimizedHelp: string
  notSufficientlyMinimized: string
}

const DEFAULT_LABELS: PpwrComplianceLabels = {
  heavyMetals: 'Heavy metals',
  substancesOfConcern: 'Substances of concern',
  none: 'None present',
  sufficientlyMinimized: 'Present but sufficiently minimised',
  heavyMetalsSufficientlyMinimizedHelp:
    'Sum of concentration levels of lead, cadmium, mercury, and hexavalent chromium that does not exceed 100 mg/kg.',
  notSufficientlyMinimized: 'Present and not sufficiently reduced',
}

const LEVEL_LABEL_KEYS: Record<
  PackagingPpwrComplianceLevel,
  keyof Pick<
    PpwrComplianceLabels,
    'none' | 'sufficientlyMinimized' | 'notSufficientlyMinimized'
  >
> = {
  NONE: 'none',
  SUFFICIENTLY_MINIMIZED: 'sufficientlyMinimized',
  NOT_SUFFICIENTLY_MINIMIZED: 'notSufficientlyMinimized',
}

type ComplianceSectionProps = {
  name: string
  title: string
  value: PackagingPpwrComplianceLevel | null | undefined
  onChange: (value: PackagingPpwrComplianceLevel) => void
  labels: PpwrComplianceLabels
  sufficientlyMinimizedHelp?: string
  disabled?: boolean
}

function ComplianceSection({
  name,
  title,
  value,
  onChange,
  labels,
  sufficientlyMinimizedHelp,
  disabled = false,
}: ComplianceSectionProps) {
  return (
    <div>
      <span className='mb-2 block text-sm font-medium'>{title}</span>
      <RadioGroup
        value={value ?? ''}
        onValueChange={(nextValue) => {
          onChange(nextValue as PackagingPpwrComplianceLevel)
        }}
        disabled={disabled}
        className='flex flex-col gap-2'
      >
        {PACKAGING_PPWR_COMPLIANCE_LEVELS.map((level) => {
          const itemId = `${name}-${level}`
          const showSufficientlyMinimizedHelp =
            level === 'SUFFICIENTLY_MINIMIZED' && sufficientlyMinimizedHelp
          return (
            <div key={level} className='flex items-start gap-2'>
              <RadioGroupItem value={level} id={itemId} className='mt-0.5' />
              <div className='space-y-1'>
                <Label
                  htmlFor={itemId}
                  className='cursor-pointer text-sm font-normal'
                >
                  {labels[LEVEL_LABEL_KEYS[level]]}
                </Label>
                {showSufficientlyMinimizedHelp && (
                  <p className='text-xs text-muted-foreground'>
                    {sufficientlyMinimizedHelp}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </RadioGroup>
    </div>
  )
}

type PackagingPpwrComplianceFieldsProps = {
  heavyMetalsStatus: PackagingPpwrComplianceLevel | null | undefined
  substancesOfConcernStatus: PackagingPpwrComplianceLevel | null | undefined
  onHeavyMetalsChange: (value: PackagingPpwrComplianceLevel) => void
  onSubstancesChange: (value: PackagingPpwrComplianceLevel) => void
  labels?: PpwrComplianceLabels
  disabled?: boolean
  showHeavyMetals?: boolean
  showSubstancesOfConcern?: boolean
}

export function PackagingPpwrComplianceFields({
  heavyMetalsStatus,
  substancesOfConcernStatus,
  onHeavyMetalsChange,
  onSubstancesChange,
  labels = DEFAULT_LABELS,
  disabled = false,
  showHeavyMetals = true,
  showSubstancesOfConcern = true,
}: PackagingPpwrComplianceFieldsProps) {
  return (
    <div className='space-y-4'>
      {showHeavyMetals && (
        <ComplianceSection
          name='heavy-metals'
          title={labels.heavyMetals}
          value={heavyMetalsStatus}
          onChange={onHeavyMetalsChange}
          labels={labels}
          sufficientlyMinimizedHelp={labels.heavyMetalsSufficientlyMinimizedHelp}
          disabled={disabled}
        />
      )}
      {showSubstancesOfConcern && (
        <ComplianceSection
          name='substances-of-concern'
          title={labels.substancesOfConcern}
          value={substancesOfConcernStatus}
          onChange={onSubstancesChange}
          labels={labels}
          disabled={disabled}
        />
      )}
    </div>
  )
}
